import { useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import {
  ExternalLinkIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { Link, useOutletContext, useParams } from 'react-router-dom';

import { MailSequenceScheduledSendStatus } from '../../../../generated/graphql';

import ActionButton from '../../../components/ActionButton';
import ActionButtonGroup from '../../../components/ActionButtonGroup';
import Editor from '../../../components/Editor';
import { defaultTransitionProps } from '../../../lib/animation';
import { formatScheduledMailAt } from '../../../lib/time';
import Spinner from '../../../svgs/Spinner';
import MailStepBadge from '../../../components/MailStepBadge';
import ScheduledSendResources from '../Sequences/ScheduledSendResources';
import SkipMailConfirmModal from '../Sequences/SkipMailConfirmModal';
import EditScheduledMailModal from './EditScheduledMailModal';
import { FollowUpsOutletContext, UpcomingScheduledMail } from './types';

const ScheduledMailDetails = () => {
  const { scheduledSendId } = useParams();
  const {
    patient,
    upcomingScheduledMails,
    enrollmentsLoading,
    refetchEnrollments,
  } = useOutletContext<FollowUpsOutletContext>();
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const scheduledMail: UpcomingScheduledMail | undefined = useMemo(
    () => upcomingScheduledMails.find((mail) => mail.id === scheduledSendId),
    [upcomingScheduledMails, scheduledSendId],
  );

  if (enrollmentsLoading && !scheduledMail) {
    return <Spinner className="mx-auto mt-8" />;
  }

  if (!scheduledMail) {
    return null;
  }

  const scheduleLabel = formatScheduledMailAt(
    scheduledMail.scheduledDate,
    scheduledMail.defaultSendTime,
  );

  const sequencePath = patient?.programInstanceId
    ? `/clients/${patient.programInstanceId}/sequences/${scheduledMail.enrollmentId}`
    : undefined;

  const canEdit =
    scheduledMail.status === MailSequenceScheduledSendStatus.Pending ||
    scheduledMail.status === MailSequenceScheduledSendStatus.Failed;
  const canSkip = canEdit;

  return (
    <>
      <Transition
        show
        {...defaultTransitionProps}
        className="h-full overflow-y-auto overscroll-y-contain xl:flex-grow"
      >
        <div className="flex w-full flex-col p-6 pb-24 text-neutral-150">
          <div className="mb-2 flex w-full flex-row items-start justify-between gap-x-6">
            <div className="min-w-0 flex-1 font-serif text-subtitle-small font-light line-clamp-3">
              {scheduledMail.subject || 'Scheduled mail'}
            </div>
            {(canEdit || canSkip) && (
              <div className="flex shrink-0">
                <ActionButtonGroup size="small">
                  {canEdit && (
                    <ActionButton
                      size="small"
                      aria-label="Edit mail"
                      title="Edit mail"
                      onClick={() => setEditOpen(true)}
                    >
                      <PencilIcon className="h-4 w-4 text-neutral-125" />
                    </ActionButton>
                  )}
                  {canSkip && (
                    <ActionButton
                      size="small"
                      aria-label="Skip mail"
                      title="Skip mail"
                      onClick={() => setConfirmSkip(true)}
                    >
                      <TrashIcon className="h-4 w-4 text-red-100" />
                    </ActionButton>
                  )}
                </ActionButtonGroup>
              </div>
            )}
          </div>
          <div className="mb-6 flex flex-col gap-y-1 text-caption leading-snug text-neutral-110">
            <div className="flex flex-wrap items-baseline gap-x-1">
              <MailStepBadge
                stepIndex={scheduledMail.stepIndex}
                size="small"
                className="font-medium text-neutral-125"
              />
              <span>from sequence:</span>
              {sequencePath ? (
                <Link
                  to={sequencePath}
                  className="inline-flex items-center gap-1.5 font-medium text-secondary-125 hover:text-secondary-150 hover:underline"
                >
                  {scheduledMail.sequenceTitle}
                  <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden />
                </Link>
              ) : (
                <span className="font-medium text-neutral-125">
                  {scheduledMail.sequenceTitle}
                </span>
              )}
            </div>
            <div>
              <span className="font-medium text-neutral-125">Scheduled:</span>{' '}
              {scheduleLabel}
              {scheduledMail.status ===
                MailSequenceScheduledSendStatus.Sending && (
                <span className="ml-2 italic">(sending…)</span>
              )}
              {scheduledMail.status ===
                MailSequenceScheduledSendStatus.Failed && (
                <span className="ml-2 text-red-150">
                  {scheduledMail.failureReason || 'Send failed'}
                </span>
              )}
            </div>
          </div>

          {scheduledMail.providerNote ? (
            <div className="mb-6">
              <Editor
                readOnly
                initialContentMarkdown={scheduledMail.providerNote}
                elKey={`scheduled-mail-note-${scheduledMail.id}-${scheduledMail.subject}-${scheduledMail.providerNote.length}`}
                className="prose prose-sm max-w-none break-words text-caption"
                containerClassName="w-full"
              />
            </div>
          ) : (
            <div className="mb-6 flex w-full items-center justify-center whitespace-pre-wrap rounded-lg border border-neutral-75 p-4 text-caption font-medium text-neutral-100">
              No note provided
            </div>
          )}

          <ScheduledSendResources
            programFollowUpId={scheduledMail.programFollowUpId}
            activities={scheduledMail.activities}
          />
        </div>
      </Transition>

      <EditScheduledMailModal
        isOpen={editOpen}
        setClosed={() => setEditOpen(false)}
        scheduledMail={scheduledMail}
        onSuccess={() => refetchEnrollments()}
      />

      <SkipMailConfirmModal
        isOpen={confirmSkip}
        scheduledSendId={scheduledMail.id}
        onClose={() => setConfirmSkip(false)}
        onSkipped={() => refetchEnrollments()}
      />
    </>
  );
};

export default ScheduledMailDetails;
